import React, { useMemo, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";

type Props = {
  show: boolean;
  handleClose: () => void;
  handleAddWidget: (content: string, type: string) => Promise<void> | void;
};

const WIDGET_TYPES = [
  { value: "alerts", label: "Alerts / Notifications" },
  { value: "fleet_state", label: "Fleet State (Pie)" },
  { value: "fleet_counter", label: "Fleet Counter (Card)" },
  { value: "fleet_co2", label: "Fleet CO2 (Chart)" },
  { value: "stats", label: "Stats (Speedometer)" },
];

// ✅ exemples JSON par type (pour que tu copies/colles vite)
const DEFAULT_JSON_BY_TYPE: Record<string, string> = {
  alerts: JSON.stringify({ Notifications: [] }, null, 2),
  fleet_state: JSON.stringify(
    { DrivingValue: 5, ParkingValue: 10, ParkingEngineRunningValue: 2, LastTransmissionValue: 0 },
    null,
    2
  ),
  fleet_counter: JSON.stringify(
    { numberOfItem: 42, title: "Vehicles", icon: "car", color: "bg-primary", linkTo: "/vehicles" },
    null,
    2
  ),
  fleet_co2: JSON.stringify({ data: {} }, null, 2),
  stats: JSON.stringify({ psn: "" }, null, 2),
};

export default function AddWidgetModal({ show, handleClose, handleAddWidget }: Props) {
  const [type, setType] = useState<string>("fleet_counter");
  const [content, setContent] = useState<string>(DEFAULT_JSON_BY_TYPE["fleet_counter"]);

  const canSubmit = useMemo(() => type.trim().length > 0, [type]);

  const onTypeChange = (newType: string) => {
    setType(newType);
    setContent(DEFAULT_JSON_BY_TYPE[newType] ?? "{}");
  };

  const onSubmit = async () => {
    if (!canSubmit) return;

    // validation JSON simple (sinon ton WidgetRenderer aura config vide)
    try {
      JSON.parse(content || "{}");
    } catch {
      alert("JSON invalide dans le champ 'Configuration'.");
      return;
    }

    await handleAddWidget(content, type);

    // reset
    setType("fleet_counter");
    setContent(DEFAULT_JSON_BY_TYPE["fleet_counter"]);
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Ajouter un widget</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label>Type</Form.Label>
          <Form.Select value={type} onChange={(e) => onTypeChange(e.target.value)}>
            {WIDGET_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        <Form.Group className="mb-2">
          <Form.Label>Configuration (JSON)</Form.Label>
          <Form.Control
            as="textarea"
            rows={10}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            style={{ fontFamily: "monospace" }}
          />
          <Form.Text className="text-muted">
            Le dashboard stocke ce JSON dans <code>widget.content</code> puis l’utilise pour injecter les props.
          </Form.Text>
        </Form.Group>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Annuler
        </Button>
        <Button variant="primary" onClick={onSubmit} disabled={!canSubmit}>
          Ajouter
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
